import posthog from "posthog-js";

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com";
const ENV = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? "local";

export const ANALYTICS_CONSENT_COOKIE = "ftk_analytics_consent";

export function getAnalyticsConsent(): "yes" | "no" | null {
  if (typeof document === "undefined") return null;
  const entry = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${ANALYTICS_CONSENT_COOKIE}=`));
  if (!entry) return null;
  const val = entry.slice(ANALYTICS_CONSENT_COOKIE.length + 1);
  return val === "yes" || val === "no" ? val : null;
}

export function setAnalyticsConsent(value: "yes" | "no"): void {
  const maxAge = 365 * 24 * 60 * 60;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ANALYTICS_CONSENT_COOKIE}=${value}; Max-Age=${maxAge}; path=/; SameSite=Lax${secure}`;
}

export function initAnalytics(): void {
  if (!KEY) return;
  const consent = getAnalyticsConsent();
  // Nothing is captured or persisted until the user explicitly accepts.
  posthog.init(KEY, {
    api_host: HOST,
    opt_out_capturing_by_default: consent !== "yes",
    persistence: consent === "yes" ? "localStorage+cookie" : "memory",
    autocapture: true,
    capture_pageview: false,
  });
  posthog.register({ environment: ENV });
}

export function acceptAnalytics(): void {
  setAnalyticsConsent("yes");
  posthog.set_config({ persistence: "localStorage+cookie" });
  // opt_in_capturing records the current page, which would otherwise be lost
  // because PageViewTracker already fired while capturing was off.
  posthog.opt_in_capturing({ captureEventName: "$pageview" });
  if (lastIdentity) posthog.identify(lastIdentity.userId, lastIdentity.traits);
}

export function declineAnalytics(): void {
  setAnalyticsConsent("no");
  posthog.opt_out_capturing();
}

export function pageview(path: string): void {
  posthog.capture("$pageview", { $current_url: window.location.origin + path });
}

// Remembered so accepting the banner later still links this session to the user;
// identify() is dropped silently while capturing is opted out.
let lastIdentity: { userId: string; traits?: Record<string, unknown> } | null = null;

export function identify(userId: string, traits?: Record<string, unknown>): void {
  lastIdentity = { userId, traits };
  posthog.identify(userId, traits);
}

export function trackEvent(name: string, props?: Record<string, unknown>): void {
  posthog.capture(name, props);
}
